import 'mapbox-gl/dist/mapbox-gl.css';
import React from 'react';
import classnames from 'classnames';
import _ from 'lodash';
import PropTypes from 'prop-types';
import ReactMapboxGl, {
    Source as MapboxSource,
    Layer as MapboxLayer
} from 'react-mapbox-gl';

import withStyles from '@material-ui/core/styles/withStyles';
import Source from './Source';
import Layer from './Layer';
import { MAIN_MAP, PRINT_MAP } from '../../redux/map/Constants';

class StandaloneMap extends React.Component {
    static propTypes = {
        classes: PropTypes.object.isRequired,
        className: PropTypes.string,
        factoryParameters: PropTypes.object,
        baseLayer: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        mapProperties: PropTypes.object,
        fitBounds: PropTypes.array,
        fitBoundsOptions: PropTypes.object,
        flyToOptions: PropTypes.object,
        activeLayers: PropTypes.array.isRequired,
        events: PropTypes.object,
        // layers: PropTypes.array.isRequired,
        // highlights: PropTypes.array.isRequired,
        // callbacks
        onMapLoaded: PropTypes.func
    };

    static defaultProps = {
        factoryParameters: {},
        events: {}
    };

    constructor(props) {
        super(props);

        this.state = {
            sources: [],
            layers: [],
            highlightLayers: [],
            refresh: {
                sources: [],
                layers: [],
                highlightLayers: []
            }
        };

        this.map = null;
        this.MapboxComponent = ReactMapboxGl({
            accessToken: 'pk.eyJ1IjoibGFuZGhhd2stdWsiLCJhIjoiY2wxa25jc3A5MDJqdDNqb2RyMHQwcXpyeSJ9.No04xFUHwcgjF8ESke5dbw',
            minZoom: 2,
            maxZoom: 22,
            ...props.factoryParameters
        });

        this.handleStyleLoad = this.handleStyleLoad.bind(this);
        this.manageFeatureStyling = this.manageFeatureStyling.bind(this);
    }

    handleStyleLoad(map) {
        const { onMapLoaded } = this.props;
        this.map = map;
        onMapLoaded && onMapLoaded(this.map);
    }

    manageFeatureStyling() {
        const { activeLayers } = this.props;
        const { refresh } = this.state;

        let sources = [];
        let layers = [];
        let highlightLayers = [];

        // ToDo Should be done in a better way, default before passed down?
        let before =
            this.props.id === MAIN_MAP || this.props.id === PRINT_MAP
                ? 'gl-draw-line-inactive.cold'
                : undefined;
        // In case draw hasn't loaded yet
        before = this.map && this.map.getLayer(before) ? before : undefined;

        if (Array.isArray(activeLayers) && activeLayers.length > 0) {
            // iterate backwards through each layer, this is because the layer index system has a first in, first shown approach (see before prop)
            // The further in the list of active layers, the higher it should appear on the map
            // We need to always leverage before because the order can change due to changing order (legend)
            for (let i = activeLayers.length - 1; i >= 0; i--) {
                const activeLayer = activeLayers[i];
                if (activeLayer.ignoreAttribution) {
                    continue;
                }
                if (!activeLayer.visible) {
                    continue;
                }

                const uid = activeLayer.uid;
                const layerData = activeLayer.layerData;
                const styleIdx = activeLayer.styleIdx || 0;
                const styling = activeLayer.styling;

                // Include all manually defined sources, part of mapbox spec
                if (styling.sources) {
                    for (const [key, value] of Object.entries(styling.sources)) {
                        sources.push(
                            <Source key={key} id={key} sourceStyling={value} />
                        );
                    }
                }

                // Custom spec appended to Mapbox, assumes all layers use the same source
                // Also assumes that the backend puts all information into layerdata
                if (styling.source) {
                    sources.push(
                        <Source
                            key={uid}
                            systemName={uid}
                            layerData={layerData}
                            sourceStyling={styling.source}
                        />
                    );
                }

                const sourceId = 'source_' + uid;
                const sourceLayer = layerData.mvt ? activeLayer.systemName : null;
                const layerId = 'layer_' + uid;

                for (
                    let layerIndex = styling.layers.length - 1;
                    layerIndex >= 0;
                    layerIndex--
                ) {
                    const id =
                        styling.layers[layerIndex]['id'] ||
                        `${layerId}_${styleIdx}_${layerIndex}`;
                    layers.push(
                        <Layer
                            key={id}
                            id={id}
                            sourceId={
                                styling.layers[layerIndex]['source'] || sourceId
                            }
                            sourceLayer={
                                styling.layers[layerIndex]['source-layer'] ||
                                sourceLayer
                            }
                            minZoom={styling.layers[layerIndex]['minzoom']}
                            maxZoom={styling.layers[layerIndex]['maxzoom']}
                            styling={{ ...styling.layers[layerIndex] }}
                            filter={styling.layers[layerIndex]['filter'] || activeLayer.layerFilter}
                            before={before}
                        />
                    );
                    before = id;
                }

                const filteredHighlights = activeLayer.highlights;
                if (activeLayer.highlights) {
                    for (let i = 0, len = filteredHighlights.length; i < len; ++i) {
                        const highlightedLayer = filteredHighlights[i];
                        // Use custom style otherwise use layer default highlight style otherwise continue
                        const highlightStyles =
                            highlightedLayer.styles || styling.highlights;
                        if (!highlightStyles) {
                            continue;
                        }
                        // Only if there is something to highlight
                        const input = highlightedLayer.features.map(
                            e => e.featureId
                        );
                        if (input.length === 0) {
                            continue;
                        }
                        // Need to create a layer for each style
                        for (let j = 0; j < highlightStyles.length; ++j) {
                            const highlightStyle = highlightStyles[j];
                            const highlightKey = `highlight_${activeLayer.uid}_${highlightedLayer.key}_${j}`;
                            highlightLayers.push(
                                <MapboxLayer
                                    id={highlightKey}
                                    key={highlightKey}
                                    filter={['in', activeLayer.primaryKey, ...input]}
                                    sourceId={sourceId}
                                    sourceLayer={sourceLayer}
                                    {...highlightStyle}
                                    // all except fill-extrusion go before the first layer
                                    before={
                                        highlightStyle.type === 'fill-extrusion'
                                            ? null
                                            : before
                                    }
                                />
                            );
                        }
                    }
                }
            }

            return { sources, layers, highlightLayers };
        } else {
            return { sources: [], layers: [], highlightLayers: [] };
        }
    }

    render() {
        const {
            classes,
            className,
            baseLayer,
            mapProperties,
            flyToOptions,
            events
        } = this.props;
        const { sources, layers, highlightLayers, refresh } = this.state;

        if (mapProperties === null) {
            return <div />;
        }

        // Loading indicator
        if (!(baseLayer && typeof baseLayer === 'object')) {
            return <div />;
        }

        const Map = this.MapboxComponent;

        const styling = this.manageFeatureStyling();
        const presentableSources = styling.sources;
        const presentableLayers = styling.layers;
        const presentableHighlightLayers = styling.highlightLayers;

        /*
            Why are we not using the baselayer as the style? Well it is a bit weird.
            Setting the style normally in Mapbox would cause added layers and sources to be refreshed, not ideal as that means the all layers can refresh when the base layer changes.
            Because of this React Mapbox library, it is smart enough to reattach the source and layers afterwards which mostly worked out. However there is complications with the
            draw library and React Mapbox library related to the before property. Changing the style(baselayer) causes for a frame the layers to be removed then re-added while state
            logic is getting performed which causes errors. As far I could tell those errors never 'broke' anything but it is probably better that the application never throws those errors.

            A simple style which is a transparent layer has been added to ensure everything still loads correctly, the style should never be set from that point onwards because of potential risks.
            The baselayers are then added like a regular layer underneath so they are always presence. This does seem to reduce re-rendering and prevents any errors from popping up. Potential issue is
            that a base layer from somewhere else can't use a custom sprite source or anything like that. This may be the correct behaviour though, as non basemap layers also use this sprite sheet and
            allowing to change could break all those other layers. May need revisiting. Another issue is that if a base layer changes, the brightness changes so every later on top should
            rerender with different color schemes. This is also needed right now.

            All this seems a bit overkill and weird but it seems to be a known use case some people need to work around on MapBox
        */
        return (
            <div className={classnames(classes.container, className)}>
                <Map
                    className={classes.map}
                    style={{
                        name: 'Background Separator',
                        glyphs: 'mapbox://fonts/geodataviz/{fontstack}/{range}.pbf',
                        layers: [
                            {
                                id: 'background-separator',
                                paint: { 'background-opacity': 0 },
                                type: 'background'
                            }
                        ],
                        sources: {},
                        sprite: window.location.origin + '/mapbox/sprites/landhawk',
                        version: 8
                    }}
                    // style={null}
                    // style={baseLayer.styling.mapbox}
                    maxBounds={[
                        [-20, 49],
                        [11, 61]
                    ]}
                    {...(mapProperties || {})}
                    // center={mapProperties.center}
                    // zoom={mapProperties.zoom}
                    // bearing={mapProperties.bearing}
                    // pitch={mapProperties.pitch}

                    fitBounds={this.props.fitBounds}
                    fitBoundsOptions={this.props.fitBoundsOptions}
                    {...events}
                    flyToOptions={flyToOptions}
                    onStyleLoad={this.handleStyleLoad}>
                    {Object.entries(baseLayer.styling.mapbox.sources).map(
                        ([key, value]) => {
                            return (
                                <MapboxSource
                                    key={key}
                                    id={key}
                                    tileJsonSource={value}
                                />
                            );
                        }
                    )}
                    {baseLayer.styling.mapbox.layers.map(layer => {
                        return (
                            <MapboxLayer
                                key={layer.id}
                                id={layer.id}
                                type={layer.type}
                                layout={layer.layout}
                                paint={layer.paint}
                                // geoJSONSourceOptions={}
                                metadata={layer.metadata}
                                sourceLayer={layer['source-layer']}
                                minZoom={layer.minzoom}
                                maxZoom={layer.maxzoom}
                                filter={layer.filter}
                                sourceId={layer.source}
                                before={'background-separator'}
                                // images={}
                            />
                        );
                    })}
                    {presentableSources}
                    {presentableLayers}
                    {presentableHighlightLayers}
                    {this.props.children}
                </Map>
            </div>
        );
    }
}

const style = theme => ({
    container: {
        height: '100%',
        position: 'relative',
        borderRadius: 'inherit'
    },
    map: {
        height: '100%',
        borderRadius: 'inherit'
    }
});

export default withStyles(style)(StandaloneMap);
